import React, { useState } from 'react';
import Card from '../UI/Card';
import Button from '../UI/Button';
import { PencilIcon } from '@heroicons/react/24/outline';
import EditProfileModal from './EditProfileModal';
import authService from '../../api/services/auth.service';

const ProfileSettings: React.FC = () => {
  const [showEditModal, setShowEditModal] = useState(false);
  
  // Get current admin from auth service
  const currentAdmin = authService.getCurrentAdmin();

  if (!currentAdmin) {
    return (
      <Card>
        <div className="text-center py-8">
          <p className="text-gray-500">No user data available</p>
        </div>
      </Card>
    );
  }

  const handleEditSuccess = () => {
    // Refresh the page or update the user data
    window.location.reload();
  };

  return (
    <Card>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Profile Settings</h2>
        <Button size="sm" onClick={() => setShowEditModal(true)}>
          <PencilIcon className="w-4 h-4 mr-1.5" />
          Edit Profile
        </Button>
      </div>

      <div className="space-y-6">
        {/* Profile Picture */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Profile Picture</label>
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-full bg-secondary-500 flex items-center justify-center text-white text-2xl font-bold">
              {currentAdmin.fullName.split(' ').map((n: string) => n[0]).join('')}
            </div>
            <div>
              <p className="text-sm text-gray-600">{currentAdmin.fullName}</p>
              <p className="text-xs text-gray-500">@{currentAdmin.username}</p>
            </div>
          </div>
        </div>

        {/* Profile Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
            <p className="text-sm text-gray-900 bg-gray-50 px-4 py-2 rounded-lg border border-gray-200">
              {currentAdmin.fullName}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
            <p className="text-sm text-gray-900 bg-gray-50 px-4 py-2 rounded-lg border border-gray-200">
              @{currentAdmin.username}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
            <p className="text-sm text-gray-900 bg-gray-50 px-4 py-2 rounded-lg border border-gray-200">
              {currentAdmin.phoneNumber}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <p className="text-sm text-gray-900 bg-gray-50 px-4 py-2 rounded-lg border border-gray-200">
              {currentAdmin.email || 'N/A'}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <p className="text-sm text-gray-900 bg-gray-50 px-4 py-2 rounded-lg border border-gray-200 capitalize">
              {currentAdmin.role.replace('_', ' ')}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <p className="text-sm text-gray-900 bg-gray-50 px-4 py-2 rounded-lg border border-gray-200">
              <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                currentAdmin.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
              }`}>
                {currentAdmin.status === 'active' ? 'Active' : 'Inactive'}
              </span>
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Member Since</label>
            <p className="text-sm text-gray-900 bg-gray-50 px-4 py-2 rounded-lg border border-gray-200">
              {new Date(currentAdmin.createdAt).toLocaleDateString()}
            </p>
          </div>
        </div>

        {/* Edit Profile Modal */}
        <EditProfileModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          adminData={currentAdmin}
          onSuccess={handleEditSuccess}
        />
      </div>
    </Card>
  );
};

export default ProfileSettings;